import axios from "./axios";

/** Public marketplace table — search / industry filter / sort / paging. */
export const getAffiliateMarketplace = async ({
  search = "",
  industry = "",
  sort = "affiliateEarnings",
  order = "desc",
  page = 1,
  limit = 10,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (search.trim()) params.append("search", search.trim());
    if (industry) params.append("industry", industry);
    params.append("sort", sort);
    params.append("order", order);
    params.append("page", page);
    params.append("limit", limit);
    const response = await axios.get(`/affiliate/marketplace?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAffiliateProgram = async (programId) => {
  try {
    const response = await axios.get(`/affiliate/programs/${programId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Become an affiliate — returns the referral code + shareable link. */
export const joinAffiliateProgram = async (programId) => {
  try {
    const response = await axios.post(`/affiliate/programs/${programId}/join`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const leaveAffiliateProgram = async (programId) => {
  try {
    const response = await axios.delete(`/affiliate/programs/${programId}/join`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** The caller's own links + performance. */
export const getMyAffiliateLinks = async () => {
  try {
    const response = await axios.get("/affiliate/me");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getMyAffiliateReferrals = async () => {
  try {
    const response = await axios.get("/affiliate/me/referrals");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Count a click on /r/:code and get the mint space it points at. */
export const trackAffiliateClick = async (code, visitorId) => {
  try {
    const response = await axios.post(`/affiliate/track/${code}`, {
      visitorId,
      referer: typeof document !== "undefined" ? document.referrer : "",
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const resolveAffiliateCode = async (code) => {
  try {
    const response = await axios.get(`/affiliate/resolve/${code}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ---------------------------------------------------------------------------
// Mint Space owner side
// ---------------------------------------------------------------------------

/** Omit productId to get the space's first product (and the full product list either way). */
export const getMintSpaceAffiliateProgram = async (mintSpaceId, productId = "") => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/affiliate`, {
      params: productId ? { productId } : {},
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const saveMintSpaceAffiliateProgram = async (mintSpaceId, payload) => {
  try {
    const response = await axios.put(`/mint-space/${mintSpaceId}/affiliate`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Omit productId for every affiliate in the space, across all products. */
export const getMintSpaceAffiliates = async (mintSpaceId, productId = "") => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/affiliate/affiliates`, {
      params: productId ? { productId } : {},
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateMintSpaceAffiliate = async (mintSpaceId, affiliateId, action) => {
  try {
    const response = await axios.put(
      `/mint-space/${mintSpaceId}/affiliate/affiliates/${affiliateId}`,
      { action }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Make a specific person an affiliate of one product; their link works straight away. */
export const addMintSpaceAffiliate = async (mintSpaceId, productId, userId) => {
  try {
    const response = await axios.post(`/mint-space/${mintSpaceId}/affiliate/affiliates`, {
      productId,
      userId,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** People the owner could add, by username or business name (2+ characters). */
export const searchMintSpaceAffiliateCandidates = async (mintSpaceId, q, productId = "") => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/affiliate/candidates`, {
      params: productId ? { q, productId } : { q },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/** Omit productId for every conversion in the space, across all products. */
export const getMintSpaceAffiliateReferrals = async (mintSpaceId, productId = "") => {
  try {
    const response = await axios.get(`/mint-space/${mintSpaceId}/affiliate/referrals`, {
      params: productId ? { productId } : {},
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
